import { STYLES } from "@/data/playalongStyles";
import { barChords, chordStarts, keyPc } from "@/lib/playalongEngine";
import { hasMelody, barKind, hitBar } from "@/data/playalong";

const SHARPS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLATS = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const FLAT_KEYS = new Set([1, 3, 5, 8, 10]);
const PCS = { C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11 };

const toMidi = (n) => { const m = n.match(/^([A-G][b#]?)(\d)$/); return (Number(m[2]) + 1) * 12 + PCS[m[1]]; };
const nameOf = (midi, keyRoot) => `${(FLAT_KEYS.has(keyRoot) ? FLATS : SHARPS)[midi % 12]}${Math.floor(midi / 12) - 1}`;
const fit = (m, lo, hi) => { let n = m; while (n < lo) n += 12; while (n > hi) n -= 12; return n; };

const DRUM_PITCH = { k: "F4", s: "C5", h: "G5", r: "F5", t1: "E5", t2: "D5", t3: "A4", c: "A5" };

// Rests fill any silent start of a beat; a silent bar gets a whole bar's rest.
function withRests(events, hitAt, steps, sub) {
  if (!events.length) return [{ step: 0, len: steps, rest: true, bar: true }];
  const rests = [];
  for (let b0 = 0; b0 < steps; b0 += sub) {
    let f = b0;
    while (f < b0 + sub && !hitAt(f)) f += 1;
    if (f === b0) continue;
    const prev = rests[rests.length - 1];
    const whole = f - b0 === sub;
    if (whole && prev && prev.len === sub && prev.step + sub === b0 && steps === 4 * sub && prev.step % (2 * sub) === 0) prev.len = 2 * sub;
    else rests.push({ step: b0, len: f - b0, rest: true });
  }
  return [...events, ...rests].sort((a, b) => a.step - b.step);
}

// Splits a bar's chords into [{chord, step, len}] (two = half bar each; three = beat 1, beat 2, second half).
const chordSegments = (bar, keyRoot, steps) => {
  const cs = barChords(bar, keyRoot);
  const starts = chordStarts(cs.length, steps);
  return cs.slice(0, starts.length).map((c, i) => ({ c, step: starts[i], len: (starts[i + 1] ?? steps) - starts[i] }));
};

export function buildScore(track, instrumentId, semitones) {
  const style = STYLES[track.style];
  const { steps, sub } = style;
  const keyRoot = (keyPc(track.key) + semitones + 12) % 12;
  const drums = instrumentId === "drummers";
  const bars = track.bars.map((bar, bi) => {
    const segs = chordSegments(bar, keyRoot, steps);
    const chords = segs.map((s) => ({ label: s.c.label, step: s.step }));
    const kind = barKind(track, bi, style);
    let events = [];
    if (drums) {
      const pat = kind === "hit" ? hitBar(steps) : kind === "fill" ? { ...style.drums, ...style.fill } : style.drums;
      const at = Object.entries(pat).map(([, line]) => line);
      const hitAt = (st) => at.some((line) => line[st] && line[st] !== ".");
      for (let st = 0; st < steps; st += 1) {
        const hits = Object.entries(pat).filter(([, line]) => line[st] && line[st] !== ".");
        if (!hits.length) continue;
        // Written length: up to the next hit, but never across the beat.
        const beatEnd = (Math.floor(st / sub) + 1) * sub;
        let nx = st + 1;
        while (nx < beatEnd && !hitAt(nx)) nx += 1;
        events.push({ step: st, len: nx - st, pitches: hits.map(([v]) => DRUM_PITCH[v]), heads: hits.map(([v]) => (["h", "r", "c"].includes(v) ? "x" : "normal")),
          ghost: hits.every(([, l]) => l[st] === "g"), accent: hits.some(([, l]) => l[st] === "a") });
      }
      events = withRests(events, hitAt, steps, sub);
    } else if (kind === "hit" && !hasMelody(track)) {
      const c = segs[0].c;
      const first = instrumentId === "singers" ? [nameOf(fit(60 + c.root + c.intervals[1], 62, 74), keyRoot)]
        : c.intervals.map((iv) => nameOf(fit(60 + c.root + iv, 57, 77), keyRoot)).sort((a, b) => toMidi(a) - toMidi(b));
      events = withRests([{ step: 0, len: sub, pitches: first }], (st) => st === 0, steps, sub);
    } else if (hasMelody(track)) {
      let pos = 0;
      events = track.melody[bi].map(([n, d]) => {
        const len = Math.round(d * sub);
        const ev = n === "r" ? { step: pos, len, rest: true } : { step: pos, len, pitches: [nameOf(toMidi(n) + semitones, keyRoot)] };
        pos += len;
        return ev;
      });
    } else if (instrumentId === "singers") {
      events = segs.map((s) => ({ step: s.step, len: s.len, pitches: [nameOf(fit(60 + s.c.root + s.c.intervals[1], 62, 74), keyRoot)] }));
    } else {
      events = segs.map((s) => ({ step: s.step, len: s.len, pitches: s.c.intervals.map((iv) => nameOf(fit(60 + s.c.root + iv, 57, 77), keyRoot)).sort((a, b) => toMidi(a) - toMidi(b)) }));
    }
    return { chords, events };
  });
  return { bars, steps, sub, clef: drums ? "percussion" : "treble", perSystem: drums ? 2 : 4, drums,
    caption: drums ? "Your drum part" : hasMelody(track) ? "Melody" : instrumentId === "singers" ? "Sing-along guide line (3rd of each chord)" : "Chord voicings" };
}

// Length in quarter notes for notation.
export const quarterLen = (len, sub) => (sub === 4 ? len / 4 : len / 2);
